import React, { useMemo, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { inventoryApi } from 'api';
import { toastApiError } from 'utils/apiErrors';

const MAX_QUERY_DAYS = 92;

const todayIST = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const get = type => parts.find(part => part.type === type).value;
  return `${get('year')}-${get('month')}-${get('day')}`;
};

const StockLedger = () => {
  const today = todayIST();
  const firstOfMonth = `${today.slice(0, 8)}01`;

  const [startDate, setStartDate] = useState(firstOfMonth);
  const [endDate, setEndDate] = useState(today);
  const [requesting, setRequesting] = useState(false);

  const rangeError = useMemo(() => {
    if (!startDate || !endDate) return 'Start date and end date are required.';
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (start > end) return 'Start date must be before or equal to end date.';
    // matches the backend rule: (end - start).days > 92 is rejected
    const diffDays = (end - start) / (1000 * 60 * 60 * 24);
    if (diffDays > MAX_QUERY_DAYS)
      return `Maximum query range is ${MAX_QUERY_DAYS} days.`;
    return null;
  }, [startDate, endDate]);

  const handleExport = async () => {
    if (rangeError || requesting) return;

    try {
      setRequesting(true);
      await inventoryApi.getInventoryLedgerExport({ startDate, endDate });
      toast.success('Export successfully');
    } catch (error) {
      toastApiError(error, 'Failed to export stock ledger.');
    } finally {
      setRequesting(false);
    }
  };

  return (
    <Card>
      <Card.Header className="bg-body-tertiary">
        <h5 className="mb-0">Stock Ledger</h5>
      </Card.Header>
      <Card.Body>
        <p className="text-600 mb-3">
          Export daily stock in/out movements by SKU. The file is prepared in
          the background — once it is ready, download it from the Exports menu
          in the top bar and it will be saved as an Excel file with Summary and
          Detail sheets. Maximum range is {MAX_QUERY_DAYS} days.
        </p>
        <Row className="align-items-center g-2">
          <Col xs="auto" className="d-flex align-items-center gap-2">
            <Form.Control
              type="date"
              size="sm"
              value={startDate}
              max={endDate || undefined}
              disabled={requesting}
              onChange={e => setStartDate(e.target.value)}
            />
            <span className="text-600">~</span>
            <Form.Control
              type="date"
              size="sm"
              value={endDate}
              min={startDate || undefined}
              disabled={requesting}
              onChange={e => setEndDate(e.target.value)}
            />
            <Button
              size="sm"
              variant="primary"
              className="text-nowrap"
              disabled={!!rangeError || requesting}
              onClick={handleExport}
            >
              Export
            </Button>
          </Col>
        </Row>
        {rangeError && startDate && endDate && (
          <div className="text-danger fs-10 mt-2">{rangeError}</div>
        )}
      </Card.Body>
    </Card>
  );
};

export default StockLedger;
